'use client';

import {
  AnalyticsSkuViewed,
  type AnalyticsSkuViewedProps,
} from '@cc-shopping/platform-analytics/AnalyticsSkuViewed';
import { resolveActivationOdds } from 'src/features/approval-odds/ActivationBadge/resolveActivationOdds';
import { useIsApprovalOddsPage } from 'src/features/approval-odds/analytics/useIsApprovalOddsPage';
import { useApprovalLikelihoodContext } from 'src/features/approval-odds/context';
import { useCardPosition } from 'src/features/roundup-filters/CardPositionContext';
import { useFiltersDialogContext } from 'src/features/roundup-filters/Mobile/components/FiltersDialogContext';
import {
  type ExperimentOfferData,
  ExperimentOfferProvider,
} from 'src/shared/providers/ExperimentOfferContext';

export function ProductCardAnalyticsWrapper({
  children,
  skuViewedProps,
  productId,
  experimentOfferData,
  wrapperProps,
}: {
  children: React.ReactNode;
  skuViewedProps: AnalyticsSkuViewedProps;
  productId: string;
  experimentOfferData?: ExperimentOfferData;
  wrapperProps: React.ComponentProps<'div'> & Record<`data-${string}`, string>;
}) {
  const { isMobileFiltersDialogOpen } = useFiltersDialogContext();
  const isApprovalOddsPage = useIsApprovalOddsPage();
  const { approvalLikelihoods } = useApprovalLikelihoodContext();
  const impressionPosition = useCardPosition(skuViewedProps.impressionPosition);
  const approvalOdds =
    resolveActivationOdds(approvalLikelihoods, productId)?.approvalLikelihood ?? null;
  const skuViewedPropsWithOdds: AnalyticsSkuViewedProps = {
    ...skuViewedProps,
    ...(isApprovalOddsPage && {
      additionalAttributes: {
        ...skuViewedProps.additionalAttributes,
        additional_attributes: { approval_odds: approvalOdds },
      },
    }),
  };

  return (
    <ExperimentOfferProvider value={experimentOfferData ?? null}>
      <AnalyticsSkuViewed
        enabled={!isMobileFiltersDialogOpen}
        {...skuViewedPropsWithOdds}
        impressionPosition={impressionPosition}
      >
        <div {...wrapperProps}>{children}</div>
      </AnalyticsSkuViewed>
    </ExperimentOfferProvider>
  );
}
